import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Wallet, Apple } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { isBackgroundDark } from '@/lib/core/utils';

// Question interface for web forms
interface Question {
  id: string;
  type: string;
  text: string;
  required: boolean;
  options?: { id: string; value: string }[];
}

// UI text configuration for web forms
interface UITextConfig {
  ratingTitle?: string;
  ratingSubtitle?: string;
  ratingCTA?: string;
  rewardTitle?: string;
  feedbackTitle?: string;
  emailPlaceholder?: string;
  consentText?: string;
  continueButtonText?: string;
  nextButtonText?: string;
  formTitle?: string;
  nameLabel?: string;
  namePlaceholder?: string;
  submitButtonText?: string;
  successTitle?: string;
  successMessageHigh?: string;
  successMessageLow?: string;
}

// Unified data interface that works for both creation and viewing
interface UnifiedPassData {
  // Pass type
  type?: 'loyalty' | 'coupon' | 'web_form';
  
  // Web form specific data
  questions?: Question[];
  starThreshold?: number;
  rewardEnabled?: boolean;
  uiTextConfig?: UITextConfig;
  
  // Business info
  businessName?: string;
  programName?: string;
  name?: string;
  header?: string;
  
  // Program configuration
  programType?: 'coffee_stamps' | 'retail_points' | 'restaurant_visits' | 'points' | 'stamps' | 'visits';
  rewardDescription?: string;
  description?: string;
  loyaltyPointsLabel?: string;
  memberLabel?: string;
  
  // Coupon-specific fields
  discount_type?: 'percentage' | 'fixed_amount' | 'free_item';
  discount_value?: number;
  minimum_purchase?: number;
  valid_until?: string;
  usage_limit_per_customer?: number;
  
  // Visual design
  backgroundColor?: string;
  background_color?: string;
  foregroundColor?: string;
  labelColor?: string;
  
  // Assets
  logoUrl?: string;
  logo_url?: string;
  programLogoUrl?: string;
  heroImageUrl?: string;
  hero_image_url?: string;
  
  // Requirements
  requiredCount?: number;
  pointsForReward?: number;
  stampsRequired?: number;
  
  // Customer data (for viewing existing passes)
  customerName?: string;
  currentPoints?: number;
  currentStamps?: number;
}

interface UnifiedPassPreviewProps {
  data?: UnifiedPassData;
  passData?: UnifiedPassData;
  mode?: 'creation' | 'viewing';
  showPlatformToggle?: boolean;
  defaultPlatform?: 'apple' | 'google';
  platform?: 'apple' | 'google';
  customerName?: string;
  liveUpdate?: boolean;
  programLogoPreview?: string;
  heroImagePreview?: string;

  // Click-to-edit callbacks for passes
  onBusinessNameClick?: () => void;
  onProgramLogoClick?: () => void;
  onAppleLogoClick?: () => void;
  onGoogleLogoClick?: () => void;
  onProgramNameClick?: () => void;
  onLoyaltyPointsLabelClick?: () => void;
  onMemberLabelClick?: () => void;
  onHeroImageClick?: () => void;

  // Click-to-edit callbacks for web forms
  editingEnabled?: boolean;
  onUpdateQuestion?: (id: string, field: keyof Question, value: unknown) => void;
  onUpdateUIText?: (field: keyof UITextConfig, value: string) => void;
  onAddOption?: (questionId: string) => void;
  onUpdateOption?: (questionId: string, optionId: string, value: string) => void;
  onRemoveOption?: (questionId: string, optionId: string) => void;
}

const UnifiedPassPreview: React.FC<UnifiedPassPreviewProps> = ({
  data,
  passData,
  mode = 'creation',
  showPlatformToggle = true,
  defaultPlatform = 'apple',
  platform,
  customerName,
  liveUpdate = true,
  programLogoPreview,
  heroImagePreview,

  // Pass edit callbacks
  onBusinessNameClick,
  onProgramLogoClick,
  onAppleLogoClick,
  onGoogleLogoClick,
  onProgramNameClick,
  onLoyaltyPointsLabelClick,
  onMemberLabelClick,
  onHeroImageClick,

  // Web form edit callbacks
  editingEnabled = false,
  onUpdateQuestion,
  onUpdateUIText,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
}) => {
  // Merge passData and data properly for reactivity
  const passInfo = React.useMemo(() => {
    return { ...data, ...passData };
  }, [data, passData]);

  const [selectedPlatform, setSelectedPlatform] = useState<'apple' | 'google'>(platform || defaultPlatform);
  const [hoveredElement, setHoveredElement] = useState<string | null>(null);
  const [isLogoSquare, setIsLogoSquare] = useState<boolean>(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Helper functions for hover management with debouncing
  const handleMouseEnter = (elementName: string) => {
    // Clear any pending timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredElement(elementName);
  };

  const handleMouseLeave = () => {
    // Delay clearing to prevent flickering when moving between elements
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredElement(null);
    }, 50); // 50ms delay
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Detect if the Apple logo is square by loading the image and checking dimensions
  useEffect(() => {
    const logoUrl = getLogoUrl('apple');
    if (logoUrl) {
      const img = new Image();
      img.onload = () => {
        // Check if aspect ratio is close to 1:1 (square)
        // We use a threshold to account for slight variations
        const aspectRatio = img.width / img.height;
        const isSquare = aspectRatio >= 0.9 && aspectRatio <= 1.1;
        setIsLogoSquare(isSquare);
      };
      img.onerror = () => {
        setIsLogoSquare(false);
      };
      img.src = logoUrl;
    } else {
      setIsLogoSquare(false);
    }
  }, [passInfo.apple_logo_urls]);

  // Helper functions to normalize data across different sources
  const getBusinessName = () => passInfo.businessName || passInfo.name || 'Your Business';
  const getHeaderText = () => passInfo.header || passInfo.businessName || passInfo.name || 'Your Business';
  const getProgramName = () => passInfo.programName || passInfo.name || getBusinessName();
  const getLogoUrl = (platform: 'apple' | 'google' = 'apple') => {
    if (platform === 'google') {
      // Only return Google-specific logo URL, no fallback to Apple logo
      return passInfo.google_logo_url || '';
    }
    // For Apple, use apple_logo_urls['3x'] if available, no fallback to Google logo
    return passInfo.apple_logo_urls?.['3x'] || '';
  };
  const getHeroUrl = () => heroImagePreview || passInfo.hero_image_urls?.['3x'] || passInfo.heroImageUrl || passInfo.hero_image_url;
  const getCustomerName = () => customerName || passInfo.customerName || 'John Doe';
  const getPointsLabel = () => passInfo.loyaltyPointsLabel || passInfo.description || (passInfo.type === 'coupon' ? 'Coupon Offer' : 'Reward Points');
  const getMemberLabel = () => passInfo.memberLabel || 'Member';
  const getBackgroundColor = () => passInfo.backgroundColor || passInfo.background_color || '#72461d';
  
  // Determine program type for display
  const getProgramTypeForDisplay = () => {
    const type = passInfo.programType;
    if (type === 'coffee_stamps' || type === 'restaurant_visits' || type === 'stamps') return 'stamps';
    if (type === 'retail_points' || type === 'points') return 'points';
    return 'points'; // default
  };

  // Get current balance based on mode and program type
  const getCurrentBalance = () => {
    if (mode === 'viewing') {
      const programType = getProgramTypeForDisplay();
      return programType === 'points' ? (passInfo.currentPoints || 0) : (passInfo.currentStamps || 0);
    }
    return 0; // Creation mode always shows 0
  };

  // Get required count for progress display
  const getRequiredCount = () => {
    return passInfo.requiredCount || passInfo.pointsForReward || passInfo.stampsRequired || 10;
  };

  // Get coupon-specific display text
  const getDiscountDisplay = () => {
    if (passInfo.type !== 'coupon') return '';
    
    if (passInfo.discount_type === 'percentage') {
      return `${passInfo.discount_value}% off`;
    } else if (passInfo.discount_type === 'fixed_amount') {
      return `$${passInfo.discount_value} off`;
    } else if (passInfo.discount_type === 'free_item') {
      return 'Free item';
    }
    return 'Special offer';
  };

  const getValidityText = () => {
    if (passInfo.type !== 'coupon' || !passInfo.valid_until) return '';
    
    const expiryDate = new Date(passInfo.valid_until);
    return `Valid until ${expiryDate.toLocaleDateString()}`;
  };

  // Process UI text with business name placeholder
  const processUIText = (text: string, businessName?: string) => {
    return text?.replace('{businessName}', businessName || getBusinessName()) || '';
  };

  const WebFormPreview = () => {
    if (passInfo.type !== 'web_form') return null;
    
    const questions = passInfo.questions || [];
    const uiText = passInfo.uiTextConfig || {};
    const businessName = getBusinessName();
    
    return (
      <div className="w-full mx-auto" style={{ maxWidth: '280px' }}>
        {/* iPhone Frame - matching the loyalty pass styling */}
        <div className="relative" style={{ width: 280 + 16, height: 600 + 32 }}>
          {/* Outer Shadow */}
          <div className="absolute inset-0 bg-black/20 rounded-[45px] blur-sm scale-105"></div>
          
          {/* iPhone Frame - Titanium Natural */}
          <div className="relative bg-gradient-to-br from-slate-300 via-slate-400 to-slate-500 rounded-[42px] shadow-2xl p-2">
            {/* Inner Titanium Edge */}
            <div className="w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 rounded-[36px] p-[2px]">
              {/* Screen Bezel */}
              <div className="w-full h-full bg-black rounded-[34px] p-[3px]">
                {/* Screen */}
                <div className="relative w-full h-full bg-white rounded-[31px] overflow-hidden">
                  {/* Status Bar */}
                  <div className="absolute top-0 left-0 right-0 h-10 bg-white flex items-center justify-between px-5 z-50 text-black text-sm font-medium">
                    <div className="flex items-center gap-1">
                      <span>9:41</span>
                    </div>
                    {/* Dynamic Island */}
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-black rounded-full shadow-inner">
                      <div className="absolute inset-0 bg-gradient-to-b from-gray-700/30 to-transparent rounded-full"></div>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <div className="flex items-center gap-1">
                        {/* Signal bars */}
                        <div className="flex gap-[2px] items-end">
                          <div className="w-1 h-2 bg-black rounded-full"></div>
                          <div className="w-1 h-3 bg-black rounded-full"></div>
                          <div className="w-1 h-4 bg-black rounded-full"></div>
                          <div className="w-1 h-3 bg-gray-400 rounded-full"></div>
                        </div>
                        {/* Battery */}
                        <div className="w-6 h-3 border border-black rounded-sm ml-1">
                          <div className="w-4/5 h-full bg-green-500 rounded-sm"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Home Indicator */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1.5 bg-black/30 rounded-full"></div>

                  {/* Content Area */}
                  <div className="w-full h-full pt-10 pb-6">
                    <div 
                      className="w-full h-full overflow-auto px-4" 
                      style={{ 
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                      }}
                    >
                      {/* Rating Section */}
                      <div className="text-center py-8">
                        <h1 
                          className={`text-xl font-bold mb-2 ${editingEnabled ? 'cursor-pointer hover:bg-blue-100 px-2 py-1 rounded' : ''}`}
                          onClick={() => editingEnabled && onUpdateUIText && onUpdateUIText('ratingTitle', prompt('Edit rating title:', uiText.ratingTitle) || uiText.ratingTitle || '')}
                        >
                          {processUIText(uiText.ratingTitle || 'Rate your experience', businessName)}
                        </h1>
                        <p 
                          className={`text-gray-600 mb-2 ${editingEnabled ? 'cursor-pointer hover:bg-blue-100 px-2 py-1 rounded' : ''}`}
                          onClick={() => editingEnabled && onUpdateUIText && onUpdateUIText('ratingSubtitle', prompt('Edit rating subtitle:', uiText.ratingSubtitle) || uiText.ratingSubtitle || '')}
                        >
                          {processUIText(uiText.ratingSubtitle || 'with {businessName}', businessName)}
                        </p>
                        
                        {/* Star Rating */}
                        <div className="flex justify-center gap-2 mb-4">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button key={star} className="text-2xl">
                              {star <= (passInfo.starThreshold || 4) ? '⭐' : '☆'}
                            </button>
                          ))}
                        </div>
                        
                        <p 
                          className={`text-sm text-gray-500 ${editingEnabled ? 'cursor-pointer hover:bg-blue-100 px-2 py-1 rounded' : ''}`}
                          onClick={() => editingEnabled && onUpdateUIText && onUpdateUIText('ratingCTA', prompt('Edit rating CTA:', uiText.ratingCTA) || uiText.ratingCTA || '')}
                        >
                          {uiText.ratingCTA || 'Tap a star to continue'}
                        </p>
                      </div>
                      
                      {/* Questions Preview */}
                      {questions.length > 0 && (
                        <div className="space-y-4">
                          <h2 
                            className={`text-lg font-semibold ${editingEnabled ? 'cursor-pointer hover:bg-blue-100 px-2 py-1 rounded' : ''}`}
                            onClick={() => editingEnabled && onUpdateUIText && onUpdateUIText('formTitle', prompt('Edit form title:', uiText.formTitle) || uiText.formTitle || '')}
                          >
                            {uiText.formTitle || 'Share Your Feedback'}
                          </h2>
                          {questions.slice(0, 2).map((question) => (
                            <div key={question.id} className="space-y-2">
                              <label 
                                className={`block text-sm font-medium ${editingEnabled ? 'cursor-pointer hover:bg-blue-100 px-1 py-0.5 rounded' : ''}`}
                                onClick={() => editingEnabled && onUpdateQuestion && onUpdateQuestion(question.id, 'text', prompt('Edit question:', question.text) || question.text)}
                              >
                                {question.text}
                                {question.required && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              {question.type === 'text' && (
                                <input className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="Your answer..." disabled />
                              )}
                              {question.type === 'textarea' && (
                                <textarea className="w-full px-3 py-2 border border-gray-300 rounded text-sm" rows={3} placeholder="Your feedback..." disabled />
                              )}
                              {question.type === 'multiple_choice' && (
                                <div className="space-y-2">
                                  {question.options?.slice(0, 3).map((option) => (
                                    <label key={option.id} className="flex items-center space-x-2">
                                      <input type="radio" name={question.id} className="form-radio" disabled />
                                      <span 
                                        className={`text-sm ${editingEnabled ? 'cursor-pointer hover:bg-blue-100 px-1 py-0.5 rounded' : ''}`}
                                        onClick={() => editingEnabled && onUpdateOption && onUpdateOption(question.id, option.id, prompt('Edit option:', option.value) || option.value)}
                                      >
                                        {option.value}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          {questions.length > 2 && (
                            <p className="text-sm text-gray-500 text-center">
                              ... and {questions.length - 2} more question{questions.length > 3 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Submit Button */}
                      <div className="mt-8">
                        <button 
                          className={`w-full bg-blue-600 text-white py-3 rounded-lg font-medium ${editingEnabled ? 'cursor-pointer hover:ring-2 hover:ring-blue-300' : ''}`}
                          onClick={() => editingEnabled && onUpdateUIText && onUpdateUIText('submitButtonText', prompt('Edit button text:', uiText.submitButtonText) || uiText.submitButtonText || '')}
                          disabled={!editingEnabled}
                        >
                          {uiText.submitButtonText || 'Submit Feedback'}
                        </button>
                      </div>
                      
                      {/* Form Info */}
                      <div className="mt-4 p-3 bg-gray-50 rounded text-center text-xs text-gray-600">
                        <p>📱 {questions.length} question{questions.length !== 1 ? 's' : ''}</p>
                        <p>⭐ {passInfo.starThreshold || 4}+ stars → Google Reviews</p>
                        <p>{passInfo.rewardEnabled ? '🎁 Rewards enabled' : '❌ No rewards'}</p>
                        {editingEnabled && <p className="text-green-600">✏️ Click any text to edit</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ApplePreview = () => {
    // Use the same background-aware logic for consistency
    const bgColor = getBackgroundColor();
    const isDark = isBackgroundDark(bgColor);
    const appleTextColor = isDark ? 'text-white' : 'text-black';
    const appleTextColorMuted = isDark ? 'text-white/80' : 'text-black/80';

    return (
    <div className="mx-auto" style={{ width: '270px' }}>
      <div
        className="relative w-full overflow-hidden flex flex-col"
        style={{
          backgroundColor: bgColor,
          borderRadius: '12px',
          aspectRatio: '270/424.766',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15), 0 3px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)'
        }}
        onMouseLeave={() => setHoveredElement(null)}
      >
        {/* Darkening overlay when hovering */}
        {hoveredElement && (
          <div className="absolute inset-0 bg-black/60 pointer-events-none z-10" />
        )}
        {/* Header Section */}
        <div className="flex items-center px-2.5 pt-1.5 pb-2">
          <div className="flex items-center gap-2">
            <div
              className={`relative ${(onAppleLogoClick || onProgramLogoClick) ? 'cursor-pointer group' : ''}`}
              style={{}}
              onClick={onAppleLogoClick || onProgramLogoClick}
              onMouseEnter={() => (onAppleLogoClick || onProgramLogoClick) && handleMouseEnter('appleLogo')}
              onMouseLeave={handleMouseLeave}
            >
              {/* Hover overlay - larger than logo */}
              {(onAppleLogoClick || onProgramLogoClick) && (
                <div className="absolute inset-0 -m-1 z-10" style={{
                  width: isLogoSquare ? '39.5px' : '122px',
                  height: '40px'
                }} />
              )}
              {/* Logo container - responsive to square (660x660) or rectangular (480x150) */}
              <div
                className="bg-white/20 flex items-center justify-center overflow-hidden relative z-20"
                style={{
                  width: isLogoSquare ? '37.5px' : '120px',
                  height: '37.5px',
                  ...(hoveredElement === 'appleLogo' ? { backgroundColor: bgColor, boxShadow: `0 0 0 6px ${bgColor}` } : {})
                }}
              >
                {getLogoUrl('apple') ? (
                  <img
                    src={getLogoUrl('apple')}
                    alt="Program logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-white/40 flex items-center justify-center">
                    <span className={`${appleTextColor} text-xs font-bold`}>
                      {getBusinessName().charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <span
              className={`${appleTextColor} text-xs font-medium truncate whitespace-nowrap overflow-hidden text-ellipsis relative z-20 ${onBusinessNameClick ? 'cursor-pointer' : ''}`}
              style={hoveredElement === 'businessName' ? { backgroundColor: bgColor, boxShadow: `0 0 0 4px ${bgColor}`, borderRadius: '6px' } : {}}
              onClick={onBusinessNameClick}
              onMouseEnter={() => onBusinessNameClick && handleMouseEnter('businessName')}
              onMouseLeave={handleMouseLeave}
            >
              {getHeaderText()}
            </span>
          </div>
        </div>

        {/* Hero Image Section - 1125×432 aspect ratio (125:48) */}
        <div
          className={`w-full relative z-20 ${onHeroImageClick ? 'cursor-pointer' : ''}`}
          style={hoveredElement === 'heroImage' ? { backgroundColor: bgColor, boxShadow: `0 0 0 8px ${bgColor}`, borderRadius: '8px', aspectRatio: '125/48' } : { aspectRatio: '125/48' }}
          onClick={onHeroImageClick}
          onMouseEnter={() => onHeroImageClick && handleMouseEnter('heroImage')}
          onMouseLeave={handleMouseLeave}
        >
          {getHeroUrl() ? (
            <img
              src={getHeroUrl()}
              alt="Hero image"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-white/10 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-white/40">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor"/>
              </svg>
            </div>
          )}
        </div>

        {/* Content Section - Show different content for coupons vs loyalty */}
        <div className="px-4 pt-3 pb-4">
          {passInfo.type === 'coupon' ? (
            /* Coupon Content */
            <div className="space-y-3">
              {/* Discount Display */}
              <div className="text-center">
                <div className={`${appleTextColor} text-lg font-bold truncate whitespace-nowrap overflow-hidden text-ellipsis`}>
                  {getDiscountDisplay()}
                </div>
                {passInfo.minimum_purchase && passInfo.minimum_purchase > 0 && (
                  <div className={`${appleTextColorMuted} text-xs mt-1`}>
                    Min. purchase: ${passInfo.minimum_purchase}
                  </div>
                )}
              </div>
              
              {/* Validity */}
              {getValidityText() && (
                <div className="text-center">
                  <div className={`${appleTextColorMuted} text-xs`}>
                    {getValidityText()}
                  </div>
                </div>
              )}
              
              {/* Usage Limit */}
              {passInfo.usage_limit_per_customer && passInfo.usage_limit_per_customer > 1 && (
                <div className="text-center">
                  <div className={`${appleTextColorMuted} text-xs`}>
                    Use up to {passInfo.usage_limit_per_customer} times
                  </div>
                </div>
              )}
            </div>
          ) : passInfo.type === 'coupon_simple' ? (
            /* Simple Coupon - Just offer title above QR */
            <div>
              <h2 
                className={`${appleTextColor} text-lg font-normal truncate whitespace-nowrap overflow-hidden text-ellipsis relative z-20 ${onProgramNameClick ? 'cursor-pointer' : ''}`}
                style={hoveredElement === 'programName' ? { backgroundColor: bgColor, boxShadow: `0 0 0 4px ${bgColor}`, borderRadius: '6px' } : {}}
                onClick={onProgramNameClick}
                onMouseEnter={() => onProgramNameClick && handleMouseEnter('programName')}
                onMouseLeave={handleMouseLeave}
              >
                {passInfo.programName || passInfo.name || passInfo.businessName || 'Special Offer'}
              </h2>
            </div>
          ) : (
            /* Loyalty Program Content */
            <div className="flex justify-between gap-3">
              {/* Points Section - Left side */}
              <div className="flex-1">
                <div
                  className={`${appleTextColorMuted} mb-1 truncate whitespace-nowrap overflow-hidden text-ellipsis relative z-20 ${onLoyaltyPointsLabelClick ? 'cursor-pointer' : ''}`}
                  style={{ fontSize: '9px', ...(hoveredElement === 'loyaltyLabel' ? { backgroundColor: bgColor, boxShadow: `0 0 0 4px ${bgColor}`, borderRadius: '6px' } : {}) }}
                  onClick={onLoyaltyPointsLabelClick}
                  onMouseEnter={() => onLoyaltyPointsLabelClick && handleMouseEnter('loyaltyLabel')}
                  onMouseLeave={handleMouseLeave}
                >
                  {getPointsLabel().toUpperCase()}
                </div>
                <div className={`${appleTextColor} text-base font-normal truncate whitespace-nowrap overflow-hidden text-ellipsis`}>
                  {getCurrentBalance().toString()}
                </div>
              </div>

              {/* Customer Name Section - Right side */}
              <div className="flex-1 text-right">
                <div
                  className={`${appleTextColorMuted} mb-1 relative z-20 ${onMemberLabelClick ? 'cursor-pointer' : ''}`}
                  style={{ fontSize: '9px', ...(hoveredElement === 'memberLabel' ? { backgroundColor: bgColor, boxShadow: `0 0 0 4px ${bgColor}`, borderRadius: '6px' } : {}) }}
                  onClick={onMemberLabelClick}
                  onMouseEnter={() => onMemberLabelClick && handleMouseEnter('memberLabel')}
                  onMouseLeave={handleMouseLeave}
                >
                  {getMemberLabel().toUpperCase()}
                </div>
                <div className={`${appleTextColor} text-base font-normal truncate whitespace-nowrap overflow-hidden text-ellipsis`}>
                  {getCustomerName()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Spacer to push QR code towards bottom */}
        <div style={{ flex: '0.85' }} />

        {/* QR Code / Barcode Section */}
        <div className="px-4 pb-1 flex justify-center">
          {passInfo.programType === 'dotypos_points' ? (
            /* EAN-13 Barcode for Dotypos - 85% card width, adjusted height */
            <div className="bg-white rounded px-2 py-4 flex items-center justify-center" style={{ width: '85%', height: '59px' }}>
              <svg width="100%" height="35" viewBox="0 0 200 35" fill="none" preserveAspectRatio="xMidYMid meet">
                {/* EAN-13 style barcode pattern with varied widths for realism */}
                <rect x="10" y="2" width="1.5" height="31" fill="black"/>
                <rect x="13" y="2" width="2" height="31" fill="black"/>
                <rect x="17" y="2" width="4.5" height="31" fill="black"/>
                <rect x="23" y="2" width="1.5" height="31" fill="black"/>
                <rect x="26" y="2" width="2.5" height="31" fill="black"/>
                <rect x="30" y="2" width="4" height="31" fill="black"/>
                <rect x="36" y="2" width="2" height="31" fill="black"/>
                <rect x="40" y="2" width="1.5" height="31" fill="black"/>
                <rect x="43" y="2" width="4.5" height="31" fill="black"/>
                <rect x="49" y="2" width="2.5" height="31" fill="black"/>
                <rect x="53" y="2" width="2" height="31" fill="black"/>
                <rect x="57" y="2" width="4.5" height="31" fill="black"/>
                <rect x="63" y="2" width="1.5" height="31" fill="black"/>
                <rect x="66" y="2" width="2.5" height="31" fill="black"/>
                {/* Center guard bars - slightly thicker */}
                <rect x="76" y="2" width="3" height="31" fill="black"/>
                <rect x="81" y="2" width="3" height="31" fill="black"/>
                <rect x="86" y="2" width="3" height="31" fill="black"/>
                {/* Right side */}
                <rect x="96" y="2" width="4" height="31" fill="black"/>
                <rect x="102" y="2" width="2" height="31" fill="black"/>
                <rect x="106" y="2" width="1.5" height="31" fill="black"/>
                <rect x="109" y="2" width="4.5" height="31" fill="black"/>
                <rect x="115" y="2" width="2.5" height="31" fill="black"/>
                <rect x="119" y="2" width="4" height="31" fill="black"/>
                <rect x="125" y="2" width="2" height="31" fill="black"/>
                <rect x="129" y="2" width="2.5" height="31" fill="black"/>
                <rect x="133" y="2" width="4.5" height="31" fill="black"/>
                <rect x="139" y="2" width="1.5" height="31" fill="black"/>
                <rect x="142" y="2" width="2.5" height="31" fill="black"/>
                <rect x="146" y="2" width="4" height="31" fill="black"/>
                <rect x="152" y="2" width="2" height="31" fill="black"/>
                <rect x="156" y="2" width="1.5" height="31" fill="black"/>
                <rect x="159" y="2" width="4.5" height="31" fill="black"/>
                <rect x="165" y="2" width="2.5" height="31" fill="black"/>
                <rect x="169" y="2" width="2" height="31" fill="black"/>
                <rect x="173" y="2" width="4.5" height="31" fill="black"/>
                <rect x="179" y="2" width="1.5" height="31" fill="black"/>
                <rect x="182" y="2" width="2.5" height="31" fill="black"/>
                <rect x="186" y="2" width="4" height="31" fill="black"/>
              </svg>
            </div>
          ) : (
            <div className="bg-white rounded-sm p-1.5 flex items-center justify-center" style={{ width: '130px', maxWidth: '60%' }}>
              <div className="bg-white flex items-center justify-center flex-shrink-0" style={{ width: '100%', aspectRatio: '1' }}>
                <svg width="100%" height="100%" viewBox="0 0 29 29" fill="none" preserveAspectRatio="xMidYMid meet">
                  <rect width="29" height="29" fill="white"/>
                  <path d="M0 0h7v7h-7zM8 0v1h1v2h-1v1h-1v-1h-1v-1h1v-1h-1v-1zM10 0h1v1h-1zM12 0h1v1h1v1h-1v3h-1v1h-2v-1h1v-2h1zM15 0h2v2h-1v-1h-1zM18 0h3v1h1v-1h1v2h-2v-1h-1v1h-1v1h-1zM22 0h7v7h-7zM1 1v5h5v-5zM23 1v5h5v-5zM2 2h3v3h-3zM9 2h1v1h-1zM17 2h1v1h-1v1h-1v-1h-1v-1h2zM24 2h3v3h-3zM9 3h1v2h1v1h-1v1h-2v-2h1zM13 3h1v1h-1zM10 5h1v1h-1zM14 5h1v2h-1v1h-1v-1h-1v-1h2zM17 5h2v1h-2zM8 6h1v1h-1zM19 6h1v2h1v1h-2zM11 7h1v1h-1zM13 7h1v1h1v1h-2zM16 7h1v1h-1zM0 8h1v2h1v-1h1v2h-1v1h-2zM2 8h1v1h-1zM4 8h1v1h1v1h-1v1h1v3h-1v-1h-1v1h-1v1h1v1h1v1h-1v1h-1v-1h-1v-1h-1v-1h2v-1h-1v-1h1v-1h-1v-1h1v-1h1v-1h-1v1h-1v-1h1zM6 8h2v1h1v1h1v-1h1v2h1v-1h1v1h1v-1h1v1h1v1h-5v1h-1v-1h-1v-1h-1v1h-1v1h-1v-3h1zM8 8h1v1h-1zM11 8h1v1h-1zM17 8h2v2h1v2h-1v-1h-3v-1h2v-1h-1zM20 8h1v1h-1zM22 8h1v1h1v-1h5v2h-1v-1h-1v2h-2v2h2v-1h1v1h1v1h-1v1h-1v-1h-1v1h-1v-1h-1v-1h-1v-1h-1v1h-1v1h-1v1h1v-1h1v1h1v1h1v-1h1v1h1v1h-1v1h-1v-1h-2v1h-1v1h1v2h-1v1h-1v-1h-1v-1h1v-1h-1v-1h-1v1h-1v-2h1v-1h1v-3h-1v-1h1v-1h1v-1h-1v-1h1zM21 9h1v1h-1zM7 10h1v1h-1zM14 11h1v1h-1zM16 11h1v1h1v1h-2zM10 12h2v1h-2zM13 12h1v1h-1zM7 13h1v1h1v1h-2zM9 13h1v1h-1zM12 13h1v2h-1v1h-1v-1h-1v-1h2zM14 13h1v1h-1zM19 13h1v1h-1zM10 14h1v1h-1zM25 14h1v1h1v1h-1v1h1v2h-1v1h-1v-1h-1v-1h1zM14 15h1v1h-1zM18 15h1v2h-1zM0 16h1v1h1v1h-1v1h1v-1h1v2h-1v1h1v1h-2v1h2v1h1v-1h-1v-1h1v-1h1v-1h-1v-1h2v-1h-1v-1h1v-1h1v3h1v1h-1v1h-1v-1h-1v3h-1v1h-1v-1h-1v-1h-1v-1h1v-1h-1v-1h-1v1h-1v-3h1zM13 16h1v1h-1zM20 16h2v1h-2zM6 17h1v1h-1zM12 17h1v1h1v-1h1v1h1v1h-1v1h1v1h-2v1h-1v-2h-1v-1h1v-1h-1zM16 17h2v2h-1v-1h-1zM19 17h1v1h-1zM21 18v1h1v-1zM7 19h1v1h2v-1h1v1h1v1h-1v1h-2v-1h-1v1h-1v1h-1v-1h-1v-1h2zM19 19h1v2h1v1h-1v1h-1zM16 20h1v2h-1zM14 21h1v1h-1zM22 21h7v7h-7zM0 22h7v7h-7zM8 22h1v1h1v-1h1v1h1v1h-1v1h-1v-1h-1v1h1v1h1v-1h2v1h-1v1h-2v1h-1v-1h-1v-2h1v-1h-1zM17 22h1v1h1v-1h1v2h-1v1h1v1h-1v1h-2v-1h1v-1h-1v-1h-1v-1h1zM1 23v5h5v-5zM23 23v5h5v-5zM13 23h1v1h-1zM2 24h3v3h-3zM24 24h3v3h-3zM13 25h1v1h-1zM15 25h1v1h-1zM8 26h1v1h1v-1h1v2h-1v1h-2v-1h1v-1h-1zM13 26h1v1h1v1h1v1h-2v-1h-1zM16 26h2v1h-1v1h-1zM12 27h1v1h-1zM19 27h1v1h1v1h-2zM18 28h1v1h-1z" fill="black"/>
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  const GooglePreview = () => {
    // Determine text colors based on background brightness
    const bgColor = getBackgroundColor();
    const isDark = isBackgroundDark(bgColor);
    const textColor = isDark ? 'text-white' : 'text-black';
    const textColorMuted = isDark ? 'text-white/80' : 'text-black/80';
    const placeholderTextColor = isDark ? 'text-white/40' : 'text-black/40';

    return (
    <div className="mx-auto" style={{ width: '270px' }}>
      <div
        className="relative w-full overflow-hidden flex flex-col"
        style={{
          backgroundColor: bgColor,
          borderRadius: '24px',
          aspectRatio: '270/424.766',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15), 0 3px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)'
        }}
        onMouseLeave={() => setHoveredElement(null)}
      >
        {/* Darkening overlay when hovering */}
        {hoveredElement && (
          <div className="absolute inset-0 bg-black/60 pointer-events-none z-10" />
        )}
        {/* Header Section */}
        <div className="flex items-center p-4 pb-2">
          <div className="flex items-center gap-3">
            <div
              className={`relative ${(onGoogleLogoClick || onProgramLogoClick) ? 'cursor-pointer group' : ''}`}
              style={{}}
              onClick={onGoogleLogoClick || onProgramLogoClick}
              onMouseEnter={() => (onGoogleLogoClick || onProgramLogoClick) && handleMouseEnter('googleLogo')}
              onMouseLeave={handleMouseLeave}
            >
              {/* Hover overlay - larger than logo */}
              {(onGoogleLogoClick || onProgramLogoClick) && (
                <div className="absolute inset-0 -m-1 w-8 h-8 rounded-full z-10" />
              )}
              {/* Logo container - always same size */}
              <div
                className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center overflow-hidden relative z-20"
                style={{
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  ...(hoveredElement === 'googleLogo' ? { backgroundColor: bgColor, boxShadow: `0 0 0 6px ${bgColor}` } : {})
                }}
              >
                {getLogoUrl('google') ? (
                  <img
                    src={getLogoUrl('google')}
                    alt="Program logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-white/40 rounded-full flex items-center justify-center">
                    <span className={`${textColor} text-xs font-normal`}>
                      {getBusinessName().charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <span
              className={`${textColor} text-xs font-normal truncate whitespace-nowrap overflow-hidden text-ellipsis relative z-20 ${onBusinessNameClick ? 'cursor-pointer' : ''}`}
              style={hoveredElement === 'businessName' ? { backgroundColor: bgColor, boxShadow: `0 0 0 4px ${bgColor}`, borderRadius: '6px' } : {}}
              onClick={onBusinessNameClick}
              onMouseEnter={() => onBusinessNameClick && handleMouseEnter('businessName')}
              onMouseLeave={handleMouseLeave}
            >
              {getHeaderText()}
            </span>
          </div>
        </div>

        {/* Thin gray divider line */}
        <div className="w-full h-px bg-gray-300/30"></div>

        {/* Program Name */}
        <div className="px-4 pb-2 pt-4">
          <h2
            className={`${textColor} ${passInfo.type === 'coupon_simple' ? 'text-xl font-medium' : 'text-xl font-medium'} truncate whitespace-nowrap overflow-hidden text-ellipsis relative z-20 ${onProgramNameClick ? 'cursor-pointer' : ''}`}
            style={hoveredElement === 'programName' ? { backgroundColor: bgColor, boxShadow: `0 0 0 4px ${bgColor}`, borderRadius: '6px' } : {}}
            onClick={onProgramNameClick}
            onMouseEnter={() => onProgramNameClick && handleMouseEnter('programName')}
            onMouseLeave={handleMouseLeave}
          >
            {passInfo.type === 'coupon_simple' ? (passInfo.programName || passInfo.name || 'Special Offer') : getProgramName()}
          </h2>
        </div>

        {/* Content Section - Show different content for coupons vs loyalty */}
        <div className="px-4 pt-2 pb-3">
          {passInfo.type === 'coupon' ? (
            /* Coupon Content */
            <div className="space-y-3">
              {/* Discount Display */}
              <div className="text-center">
                <div className={`${textColor} text-lg font-bold truncate whitespace-nowrap overflow-hidden text-ellipsis`}>
                  {getDiscountDisplay()}
                </div>
                {passInfo.minimum_purchase && passInfo.minimum_purchase > 0 && (
                  <div className={`${textColorMuted} text-xs mt-1`}>
                    Min. purchase: ${passInfo.minimum_purchase}
                  </div>
                )}
              </div>
              
              {/* Validity */}
              {getValidityText() && (
                <div className="text-center">
                  <div className={`${textColorMuted} text-xs`}>
                    {getValidityText()}
                  </div>
                </div>
              )}
              
              {/* Usage Limit */}
              {passInfo.usage_limit_per_customer && passInfo.usage_limit_per_customer > 1 && (
                <div className="text-center">
                  <div className={`${textColorMuted} text-xs`}>
                    Use up to {passInfo.usage_limit_per_customer} times
                  </div>
                </div>
              )}
            </div>
          ) : passInfo.type === 'coupon_simple' ? (
            /* Simple Coupon - No discount details, just offer title above QR */
            <div></div>
          ) : (
            /* Loyalty Program Content */
            <div className="flex justify-between gap-4">
              {/* Points Section - Left side */}
              <div className="flex-1">
                <div
                  className={`${textColorMuted} mb-0.5 truncate whitespace-nowrap overflow-hidden text-ellipsis relative z-20 ${onLoyaltyPointsLabelClick ? 'cursor-pointer' : ''}`}
                  style={{ fontSize: '9px', ...(hoveredElement === 'loyaltyLabel' ? { backgroundColor: bgColor, boxShadow: `0 0 0 4px ${bgColor}`, borderRadius: '6px' } : {}) }}
                  onClick={onLoyaltyPointsLabelClick}
                  onMouseEnter={() => onLoyaltyPointsLabelClick && handleMouseEnter('loyaltyLabel')}
                  onMouseLeave={handleMouseLeave}
                >
                  {getPointsLabel()}
                </div>
                <div className={`${textColor} font-normal`} style={{ fontSize: '14px' }}>
                  {getCurrentBalance().toString()}
                </div>
              </div>

              {/* Customer Name Section - Right side */}
              <div className="flex-1 text-right">
                <div
                  className={`${textColorMuted} mb-0.5 relative z-20 ${onMemberLabelClick ? 'cursor-pointer' : ''}`}
                  style={{ fontSize: '9px', ...(hoveredElement === 'memberLabel' ? { backgroundColor: bgColor, boxShadow: `0 0 0 4px ${bgColor}`, borderRadius: '6px' } : {}) }}
                  onClick={onMemberLabelClick}
                  onMouseEnter={() => onMemberLabelClick && handleMouseEnter('memberLabel')}
                  onMouseLeave={handleMouseLeave}
                >
                  {getMemberLabel().toUpperCase()}
                </div>
                <div className={`${textColor} font-normal truncate whitespace-nowrap overflow-hidden text-ellipsis`} style={{ fontSize: '14px' }}>
                  {getCustomerName()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* QR Code / Barcode Section */}
        <div className="px-4 pb-4 flex justify-center">
          {passInfo.programType === 'dotypos_points' ? (
            /* EAN-13 Barcode for Dotypos - 90% card width */
            <div className="bg-white rounded-lg px-2 py-4 flex items-center justify-center" style={{ width: '90%', height: '80px' }}>
              <svg width="100%" height="72" viewBox="0 0 200 72" fill="none" preserveAspectRatio="xMidYMid meet">
                {/* EAN-13 style barcode pattern with varied widths for realism */}
                <rect x="10" y="2" width="1.5" height="68" fill="black"/>
                <rect x="13" y="2" width="2" height="68" fill="black"/>
                <rect x="17" y="2" width="4.5" height="68" fill="black"/>
                <rect x="23" y="2" width="1.5" height="68" fill="black"/>
                <rect x="26" y="2" width="2.5" height="68" fill="black"/>
                <rect x="30" y="2" width="4" height="68" fill="black"/>
                <rect x="36" y="2" width="2" height="68" fill="black"/>
                <rect x="40" y="2" width="1.5" height="68" fill="black"/>
                <rect x="43" y="2" width="4.5" height="68" fill="black"/>
                <rect x="49" y="2" width="2.5" height="68" fill="black"/>
                <rect x="53" y="2" width="2" height="68" fill="black"/>
                <rect x="57" y="2" width="4.5" height="68" fill="black"/>
                <rect x="63" y="2" width="1.5" height="68" fill="black"/>
                <rect x="66" y="2" width="2.5" height="68" fill="black"/>
                {/* Center guard bars - slightly thicker */}
                <rect x="76" y="2" width="3" height="68" fill="black"/>
                <rect x="81" y="2" width="3" height="68" fill="black"/>
                <rect x="86" y="2" width="3" height="68" fill="black"/>
                {/* Right side */}
                <rect x="96" y="2" width="4" height="68" fill="black"/>
                <rect x="102" y="2" width="2" height="68" fill="black"/>
                <rect x="106" y="2" width="1.5" height="68" fill="black"/>
                <rect x="109" y="2" width="4.5" height="68" fill="black"/>
                <rect x="115" y="2" width="2.5" height="68" fill="black"/>
                <rect x="119" y="2" width="4" height="68" fill="black"/>
                <rect x="125" y="2" width="2" height="68" fill="black"/>
                <rect x="129" y="2" width="2.5" height="68" fill="black"/>
                <rect x="133" y="2" width="4.5" height="68" fill="black"/>
                <rect x="139" y="2" width="1.5" height="68" fill="black"/>
                <rect x="142" y="2" width="2.5" height="68" fill="black"/>
                <rect x="146" y="2" width="4" height="68" fill="black"/>
                <rect x="152" y="2" width="2" height="68" fill="black"/>
                <rect x="156" y="2" width="1.5" height="68" fill="black"/>
                <rect x="159" y="2" width="4.5" height="68" fill="black"/>
                <rect x="165" y="2" width="2.5" height="68" fill="black"/>
                <rect x="169" y="2" width="2" height="68" fill="black"/>
                <rect x="173" y="2" width="4.5" height="68" fill="black"/>
                <rect x="179" y="2" width="1.5" height="68" fill="black"/>
                <rect x="182" y="2" width="2.5" height="68" fill="black"/>
                <rect x="186" y="2" width="4" height="68" fill="black"/>
              </svg>
            </div>
          ) : (
            <div className="bg-white rounded-xl px-3 py-4 flex items-center justify-center" style={{ width: '55%', maxWidth: '150px' }}>
              <div className="w-full bg-white flex items-center justify-center flex-shrink-0" style={{ aspectRatio: '1' }}>
                {passInfo.couponId ? (
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=96x96&data=${encodeURIComponent(`${window.location.origin}/coupon-offer/${passInfo.couponId}`)}`}
                    alt="Coupon QR Code"
                    className="w-full h-full"
                  />
                ) : (
                <svg width="100%" height="100%" viewBox="0 0 29 29" fill="none" preserveAspectRatio="xMidYMid meet">
                  <rect width="29" height="29" fill="white"/>
                  <path d="M0 0h7v7h-7zM8 0v1h1v2h-1v1h-1v-1h-1v-1h1v-1h-1v-1zM10 0h1v1h-1zM12 0h1v1h1v1h-1v3h-1v1h-2v-1h1v-2h1zM15 0h2v2h-1v-1h-1zM18 0h3v1h1v-1h1v2h-2v-1h-1v1h-1v1h-1zM22 0h7v7h-7zM1 1v5h5v-5zM23 1v5h5v-5zM2 2h3v3h-3zM9 2h1v1h-1zM17 2h1v1h-1v1h-1v-1h-1v-1h2zM24 2h3v3h-3zM9 3h1v2h1v1h-1v1h-2v-2h1zM13 3h1v1h-1zM10 5h1v1h-1zM14 5h1v2h-1v1h-1v-1h-1v-1h2zM17 5h2v1h-2zM8 6h1v1h-1zM19 6h1v2h1v1h-2zM11 7h1v1h-1zM13 7h1v1h1v1h-2zM16 7h1v1h-1zM0 8h1v2h1v-1h1v2h-1v1h-2zM2 8h1v1h-1zM4 8h1v1h1v1h-1v1h1v3h-1v-1h-1v1h-1v1h1v1h1v1h-1v1h-1v-1h-1v-1h-1v-1h2v-1h-1v-1h1v-1h-1v-1h1v-1h1v-1h-1v1h-1v-1h1zM6 8h2v1h1v1h1v-1h1v2h1v-1h1v1h1v-1h1v1h1v1h-5v1h-1v-1h-1v-1h-1v1h-1v1h-1v-3h1zM8 8h1v1h-1zM11 8h1v1h-1zM17 8h2v2h1v2h-1v-1h-3v-1h2v-1h-1zM20 8h1v1h-1zM22 8h1v1h1v-1h5v2h-1v-1h-1v2h-2v2h2v-1h1v1h1v1h-1v1h-1v-1h-1v1h-1v-1h-1v-1h-1v-1h-1v1h-1v1h-1v1h1v-1h1v1h1v1h1v-1h1v1h1v1h-1v1h-1v-1h-2v1h-1v1h1v2h-1v1h-1v-1h-1v-1h1v-1h-1v-1h-1v1h-1v-2h1v-1h1v-3h-1v-1h1v-1h1v-1h-1v-1h1zM21 9h1v1h-1zM7 10h1v1h-1zM14 11h1v1h-1zM16 11h1v1h1v1h-2zM10 12h2v1h-2zM13 12h1v1h-1zM7 13h1v1h1v1h-2zM9 13h1v1h-1zM12 13h1v2h-1v1h-1v-1h-1v-1h2zM14 13h1v1h-1zM19 13h1v1h-1zM10 14h1v1h-1zM25 14h1v1h1v1h-1v1h1v2h-1v1h-1v-1h-1v-1h1zM14 15h1v1h-1zM18 15h1v2h-1zM0 16h1v1h1v1h-1v1h1v-1h1v2h-1v1h1v1h-2v1h2v1h1v-1h-1v-1h1v-1h1v-1h-1v-1h2v-1h-1v-1h1v-1h1v3h1v1h-1v1h-1v-1h-1v3h-1v1h-1v-1h-1v-1h-1v-1h1v-1h-1v-1h-1v1h-1v-3h1zM13 16h1v1h-1zM20 16h2v1h-2zM6 17h1v1h-1zM12 17h1v1h1v-1h1v1h1v1h-1v1h1v1h-2v1h-1v-2h-1v-1h1v-1h-1zM16 17h2v2h-1v-1h-1zM19 17h1v1h-1zM21 18v1h1v-1zM7 19h1v1h2v-1h1v1h1v1h-1v1h-2v-1h-1v1h-1v1h-1v-1h-1v-1h2zM19 19h1v2h1v1h-1v1h-1zM16 20h1v2h-1zM14 21h1v1h-1zM22 21h7v7h-7zM0 22h7v7h-7zM8 22h1v1h1v-1h1v1h1v1h-1v1h-1v-1h-1v1h1v1h1v-1h2v1h-1v1h-2v1h-1v-1h-1v-2h1v-1h-1zM17 22h1v1h1v-1h1v2h-1v1h1v1h-1v1h-2v-1h1v-1h-1v-1h-1v-1h1zM1 23v5h5v-5zM23 23v5h5v-5zM13 23h1v1h-1zM2 24h3v3h-3zM24 24h3v3h-3zM13 25h1v1h-1zM15 25h1v1h-1zM8 26h1v1h1v-1h1v2h-1v1h-2v-1h1v-1h-1zM13 26h1v1h1v1h1v1h-2v-1h-1zM16 26h2v1h-1v1h-1zM12 27h1v1h-1zM19 27h1v1h1v1h-2zM18 28h1v1h-1z" fill="black"/>
                </svg>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Spacer to push hero image to bottom */}
        <div className="flex-1" />

        {/* Hero Image Section - At absolute bottom - 1125×432 aspect ratio (125:48) */}
        <div
          className={`w-full mt-auto relative z-20 ${onHeroImageClick ? 'cursor-pointer' : ''}`}
          style={hoveredElement === 'heroImage' ? { backgroundColor: bgColor, boxShadow: `0 0 0 8px ${bgColor}`, borderRadius: '8px', aspectRatio: '125/48' } : { aspectRatio: '125/48' }}
          onClick={onHeroImageClick}
          onMouseEnter={() => onHeroImageClick && handleMouseEnter('heroImage')}
          onMouseLeave={handleMouseLeave}
        >
          {getHeroUrl() ? (
            <img
              src={getHeroUrl()}
              alt="Hero image"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-white/10 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className={placeholderTextColor}>
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor"/>
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  // Handle web form type - return the WebFormPreview component
  if (passInfo.type === 'web_form') {
    return (
      <WebFormPreview 
        questions={passInfo.questions || []}
        starThreshold={passInfo.starThreshold || 4}
        rewardEnabled={passInfo.rewardEnabled || false}
        businessName={businessInfo?.name || "Your Business"}
        uiTextConfig={passInfo.uiTextConfig}
        onUpdateQuestion={passInfo.onUpdateQuestion}
        onUpdateUIText={passInfo.onUpdateUIText}
        onUpdateOption={passInfo.onUpdateOption}
        onRemoveOption={passInfo.onRemoveOption}
        onAddOption={passInfo.onAddOption}
        processUIText={processUIText}
      />
    );
  }

  // Render single platform without toggle (kept for backward compatibility with specific platform prop)
  if (!showPlatformToggle && platform) {
    return (
      <div className="flex justify-center">
        {selectedPlatform === 'apple' ? <ApplePreview /> : <GooglePreview />}
      </div>
    );
  }

  // Render both platforms side by side
  return (
    <div className="space-y-4">
      {/* Both Passes Side by Side */}
      <div className="flex flex-col lg:flex-row items-start justify-center gap-6 lg:gap-8">
        {/* Apple Pass */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Apple className="h-4 w-4" />
            <span>Apple Wallet</span>
          </div>
          <ApplePreview />
        </div>

        {/* Google Pass */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Wallet className="h-4 w-4" />
            <span>Google Wallet</span>
          </div>
          <GooglePreview />
        </div>
      </div>

      {/* Platform info - only show in creation mode */}
      {mode === 'creation' && (
        <div className="text-center text-sm text-gray-500">
          Preview of how the {passInfo.type === 'coupon' ? 'coupon offer' : 'loyalty card'} will appear in both wallets
        </div>
      )}
    </div>
  );
};

export default UnifiedPassPreview;